import { FC, ReactNode } from "react";
import Footer from "../common/Footer";
import Header from "../common/Header";
import UserLogin from "./UserLogin";
import UserRegister from "./UserRegister";
import UserProfile from "./UserProfile";
import UserForgotPasswordFlow from "./UserForgotPasswordFlow";
import UserDashboard from "./UserDashboard";
import UserVerifyEmailFlow from "./UserVerifyEmailFlow/UserVerifyEmailFlow";

const isModalView = (view: ViewType): boolean =>
  ["login", "register", "forgot-password", "verify-email"].includes(view);

const isLightModeView = (view: ViewType): boolean =>
  ["dashboard", "profile"].includes(view);

const viewComponents: Record<ViewType, ReactNode> = {
  login: <UserLogin />,
  register: <UserRegister />,
  dashboard: <UserDashboard />,
  "forgot-password": <UserForgotPasswordFlow />,
  "verify-email": <UserVerifyEmailFlow />,
  profile: <UserProfile />,
};

const ModalWrapper: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-md z-50">
    {children}
  </div>
);

const User: FC<UserProps> = ({ view }) => {
  const backgroundClass = isLightModeView(view)
    ? "bg-white"
    : "bg-gray-7 dark:bg-gray-7";

  const ViewComponent = viewComponents[view];

  const ComponentWithWrapper = isModalView(view) ? (
    <ModalWrapper>{ViewComponent}</ModalWrapper>
  ) : (
    ViewComponent
  );

  return (
    <div className="relative">
      <div className={`min-h-dvh h-dvh flex flex-col ${backgroundClass}`}>
        <Header
          mode={isLightModeView(view) ? "light" : "dark"}
          type={view === "dashboard" ? "admin" : "normal"}
          className="border-b border-gray-3"
        />
        <div className="flex-grow overflow-auto">{ComponentWithWrapper}</div>
        <Footer mode="dark" type="short" />
      </div>
    </div>
  );
};

export default User;

type ViewType =
  | "login"
  | "dashboard"
  | "register"
  | "profile"
  | "forgot-password"
  | "verify-email";

interface UserProps {
  view: ViewType;
}
